import React, { useState, useEffect } from 'react';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { useNavigate } from 'react-router-dom';
import PracticeManagement from '../components/admin/PracticeManagement';
import AvailabilityManagement from '../components/admin/AvailabilityManagement';
import AppointmentManagement from '../components/admin/AppointmentManagement';
import AdminControls from '../components/admin/AdminControls';
import { isAdmin } from '../utils/auth';

const AdminDashboard = ({ isProtected, setIsProtected }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('practices');
  const [isAdminUser, setIsAdminUser] = useState(false);
  const navigate = useNavigate();
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      
      if (currentUser) {
        // Check if user has admin role
        const adminStatus = await isAdmin(currentUser.uid);
        setIsAdminUser(adminStatus);
        
        if (!adminStatus) {
          // User is logged in but not an admin, redirect to user dashboard
          navigate('/user', { state: { message: 'You do not have admin privileges.' } });
        }
      } else {
        // No user is logged in, redirect to login page
        navigate('/login', { state: { redirectTo: '/admin' } });
      }
      
      setLoading(false);
    });
    
    return () => unsubscribe();
  }, [auth, navigate]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!isAdminUser) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Access Denied</h2>
          <p className="mb-4">You do not have permission to access this page.</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex items-center">
          <span className="mr-4 text-gray-600">
            Logged in as: {user?.displayName || user?.email}
          </span>
          <button
            onClick={handleLogout}
            className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
          >
            Logout
          </button>
        </div>
      </div>
      
      {/* Tabs */}
      <div className="flex border-b mb-6">
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'practices'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('practices')}
        >
          Practices
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'availability'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('availability')}
        >
          Availability
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'appointments'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('appointments')}
        >
          Appointments
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'users'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('users')}
        >
          Users
        </button>
        <button
          className={`py-2 px-4 font-medium ${
            activeTab === 'site-settings'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-blue-500'
          }`}
          onClick={() => setActiveTab('site-settings')}
        >
          Site Settings
        </button>
      </div>
      
      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {activeTab === 'practices' && <PracticeManagement />}
        {activeTab === 'availability' && <AvailabilityManagement />}
        {activeTab === 'users' && (
          <div className="text-center py-8">
            <h2 className="text-xl font-semibold mb-4">User Management</h2>
            <p className="text-gray-500">User management functionality coming soon.</p>
          </div>
        )}
        {activeTab === 'appointments' && <AppointmentManagement />}
        {activeTab === 'site-settings' && (
          <div>
            <h2 className="text-xl font-semibold mb-4">Site Settings</h2>
            <AdminControls isProtected={isProtected} setIsProtected={setIsProtected} />
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
